import {
  Component,
  inject,
  signal,
  OnInit,
  ChangeDetectorRef,
  computed,
  DestroyRef,
  effect,
  AfterViewInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngxs/store';
import {
  checkForNameErrors,
  getConsumerErrorText,
  getSubjectErrorText,
  MIN_NAME_LENGTH,
} from '../../../core/util';
import { normalizeOneRelation } from '../../../core/decorators/relation.decorator';
import { ExplanationEntity } from '../../explanation-entity';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { from, Observable, switchMap } from 'rxjs';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';
import { WidgetMessageService } from '../../../core/services/widget-message.service';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../../subject/subject.entity';
import {
  getStatusColor,
  getStatusText,
  StateFilterCBComponent,
} from '../../../utils/component/state-filter-combobox/state-cb.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ObjectConsumerMetadataComponent } from '../../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import { SequenceEntityService } from '../../../sequence/services/sequence.entity.service';
import { LearningSectionContainerContentService } from '../../../learning-section/learning-section-container/services/learning-section-container-content.entity.service';
import { LearningSectionContainerEntity } from '../../../learning-section/learning-section-container/learning-section-container-entity';
import { SequenceEntity } from '../../../sequence/sequence.entity';
import { AssetEntity } from '../../../asset/asset.entity';

@Component({
  selector: 'pixl-edit-explanation',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatCardModule,
    MatIconModule,
    FormsModule,
    CommonModule,
    TranslocoModule,
    StateFilterCBComponent,
    ObjectConsumerMetadataComponent,
  ],
  providers: [],
  templateUrl: './edit-explanation.component.html',
  styleUrl: './edit-explanation.component.scss',
})
export class EditExplanationComponent implements OnInit, AfterViewInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<EditExplanationComponent>>(MatDialogRef);

  data? = inject<Partial<ExplanationEntity>>(MAT_DIALOG_DATA);

  subjects$!: Observable<SubjectEntity[]>;
  sequences$!: Observable<SequenceEntity[]>;
  learningSectionContainers$!: Observable<LearningSectionContainerEntity[]>;
  currentPage = 1;
  pageSize = 20;

  private readonly store = inject(Store);
  private translocoService = inject(TranslocoService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private sequenceService = inject(SequenceEntityService);
  private learningSectionContainerService = inject(
    LearningSectionContainerContentService,
  );

  private widgetService = inject(WidgetMessageService);
  private destroyRef = inject(DestroyRef);
  public cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  public formGroup: FormGroup;

  consumers$ = this.store.select(
    this.consumerService.allByQuery({ sort: 'name' }),
  );

  private consumers = toSignal(this.consumers$, { initialValue: [] });
  consumerId = signal<string | null>(null);
  consumer = computed<ConsumerEntity | undefined>(() => {
    const id = this.consumerId();
    const list = this.consumers();

    if (!id || list.length === 0) return undefined;

    return list.find((c) => c.id === id);
  });

  consumerTabName = computed(() => this.consumer()?.name ?? null);
  compareConsumer = (a: string | null, b: string | null) => a === b;

  public approval = signal(false);
  public assets: AssetEntity[] = [];
  public consumerError = signal(false);
  public subjectError = signal(false);
  public isNewRecord = signal(true);
  public consumerIsDeleted = signal(false);
  public competencyIsDeleted = signal(false);

  constructor() {
    const data = this.data;

    effect(() => {
      const id = this.consumerId();
      const list = this.consumers();
      if (id && list.length) {
        const control = this.formGroup.controls.consumers;
        if (control.value !== id) {
          control.setValue(id, { emitEvent: false });
        }
      }
    });

    this.formGroup = this.fb.group({
      subjectId: new FormControl(
        { value: this.data?.subjectId || undefined, disabled: this.approval() },
        Validators.required,
      ),
      name: new FormControl(
        { value: this.data?.name || '', disabled: this.approval() },
        [Validators.required, Validators.minLength(MIN_NAME_LENGTH)],
      ),
      topics: new FormControl({
        value: this.data?.topics || [],
        disabled: this.approval(),
      }),
      assets: new FormControl({
        value: this.data?.assets || [],
        disabled: this.approval(),
      }),
      pages: new FormControl({
        value: this.data?.pages || [],
        disabled: this.approval(),
      }),
      config: new FormControl({
        value: this.data?.config || [],
        disabled: this.approval(),
      }),
      approval: new FormControl({
        value: this.data?.approval || 'Edit',
        disabled: this.approval(),
      }),
      active: new FormControl({
        value: this.data?.active || false,
        disabled: this.approval(),
      }),
      version: new FormControl({
        value: this.data?.version || 1,
        disabled: this.approval(),
      }),
      sortName: new FormControl({
        value: this.data?.sortName || 'sortName',
        disabled: this.approval(),
      }),
      updated: new FormControl({
        value: this.data?.updated || '',
        disabled: this.approval(),
      }),
      changedBy: new FormControl({
        value: this.data?.changedBy || '',
        disabled: this.approval(),
      }),
      consumerId: new FormControl(
        { value: this.data?.consumerId || '', disabled: this.approval() },
        Validators.required,
      ),
      consumers: [normalizeOneRelation(data?.consumers)],
      subjects: [normalizeOneRelation(data?.subjects)],
      sequences: [normalizeOneRelation(data?.sequences)],
      learningSectionContainers: [
        normalizeOneRelation(data?.learningSectionContainers),
      ],
    });
    this.approval.set(this.data?.approval === 'Dormant');
  }

  public ngOnInit() {
    this.formGroup.markAllAsTouched();
    this.loadRelationData();

    const control = this.formGroup.controls.consumers;

    this.consumerId.set(control.value ?? null);

    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        this.consumerId.set(id ?? null);
        this.cdr.markForCheck();
      });
  }

  private loadRelationData(): void {
    this.consumerService.ensureLoaded({ sort: 'name' });

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery)),
      ),
    );

    const sequenceQuery = { sort: 'name' };
    this.sequences$ = from(
      this.sequenceService.ensureLoaded(sequenceQuery),
    ).pipe(
      switchMap(() =>
        this.store.select(this.sequenceService.allByQuery(sequenceQuery)),
      ),
    );

    // const learningSectionContainerQuery = { sort: 'name' };
    // this.learningSectionContainers$ = from(
    //   this.learningSectionContainerService.ensureLoaded(learningSectionContainerQuery)
    // ).pipe(
    //   switchMap(() =>
    //     this.store.select(this.learningSectionContainerService.allByQuery(learningSectionContainerQuery))
    //   )
    // );
  }

  public ngAfterViewInit(): void {
    this.formGroup.markAllAsTouched();
  }

  public getConsumerError(): string {
    return getConsumerErrorText();
  }

  public getSubjectError(): string {
    return getSubjectErrorText();
  }

  public checkName(formControl: AbstractControl): string | null {
    return checkForNameErrors(formControl);
  }

  statusChanged(newStatus: string) {
    this.formGroup.get('approval')?.setValue(newStatus);
  }

  public getStateColor(): string {
    return getStatusColor(this.formGroup.value.approval);
  }

  public getStateText(): string {
    return getStatusText(this.formGroup.value.approval);
  }

  getConsumer(): ConsumerEntity {
    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    return this.consumerService.stateClass.byId(state)(this.data?.id);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    // set IDs according to selected consumer/subject
    const name = this.formGroup.value.name as string;
    const subjectId = this.formGroup.value.subjects as string;
    const consumerId = this.formGroup.value.consumers as string;
    this.formGroup.patchValue({
      sortName: name.toLocaleLowerCase(),
      subjectId: subjectId,
      consumerId: consumerId,
    });

    if (!this.formGroup.valid) {
      this.widgetService.setMessage({
        message: this.translocoService.translate('required_error'),
        messageTye: 'mtError',
      });
      return;
    }

    const formValue = this.formGroup.value;
    const filteredConsumerData = formValue.consumerData ?
      Object.fromEntries(
        Object.entries(formValue.consumerData as Record<string, unknown>).filter(([, v]) => v !== null),
      ) :
      {};
    const updatedExercise = Object.assign(
      new ExplanationEntity(),
      { id: this.data?.id },
      formValue,
      {
        consumerData: filteredConsumerData,
        consumers: formValue.consumers ? { id: formValue.consumers } : [],
        subjects: formValue.subjects ? { id: formValue.subjects } : [],
        sequences: formValue.sequences ? { id: formValue.sequences } : [],
        learningSectionContainers: formValue.learningSectionContainers ?
          { id: formValue.learningSectionContainers } :
          [],
      },
    );

    this.dialogRef.close(updatedExercise);
  }
}
